import { motion } from "framer-motion";
import { TrendingUp, ShieldCheck, Umbrella, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const iconMap = {
  TrendingUp: TrendingUp,
  ShieldCheck: ShieldCheck,
  Umbrella: Umbrella,
  Zap: Zap,
};

const Services = ({ services }) => {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      id="services"
      className="py-24 bg-slate-50"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="gold-accent mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-4 font-serif">
            Insurance Solutions That Fit Your Life
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We offer comprehensive financial products designed to protect your
            family and grow your wealth.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bento-grid"
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon] || TrendingUp;
            const isFeatured = index === 0;

            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`service-card bg-white border border-slate-100 rounded-2xl p-8 ${
                  isFeatured ? "bento-featured" : ""
                }`}
                data-testid={`service-card-${service.id}`}
              >
                <div
                  className={`flex ${
                    isFeatured ? "flex-row items-start gap-8" : "flex-col"
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`${
                      isFeatured ? "w-16 h-16" : "w-14 h-14"
                    } bg-amber-100 rounded-xl flex items-center justify-center mb-6 flex-shrink-0`}
                  >
                    <Icon
                      className={`${
                        isFeatured ? "w-8 h-8" : "w-7 h-7"
                      } text-amber-600`}
                    />
                  </div>

                  <div className="flex-1">
                    {/* Title */}
                    <h3
                      className={`${
                        isFeatured ? "text-2xl" : "text-xl"
                      } font-bold text-navy mb-3 font-serif`}
                    >
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* CTA */}
                    <Button
                      onClick={scrollToContact}
                      variant="ghost"
                      className="text-navy hover:text-amber-600 hover:bg-amber-50 p-0 h-auto font-medium group"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <Button
            onClick={scrollToContact}
            className="bg-navy hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-200 hover:shadow-lg"
            data-testid="services-cta-button"
          >
            Explore All Services
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
